import { useState, useEffect } from 'react';
import { FormControl, InputLabel, Select, MenuItem, SelectChangeEvent, CircularProgress } from '@mui/material';
import { paymentsApi, MfiCenter } from '../services/payments-api.service';

interface CenterSelectProps {
    value: string;
    onChange: (value: string, center: MfiCenter | null) => void;
    locale?: string;
    required?: boolean;
    disabled?: boolean;
    size?: 'small' | 'medium';
    fullWidth?: boolean;
    label?: string;
}

const STORAGE_KEY = 'selectedCenter';

export function CenterSelect({
    value,
    onChange,
    locale = 'en-CA',
    required = false,
    disabled = false,
    size = 'small',
    fullWidth = true,
    label = 'Centre',
}: CenterSelectProps) {
    const [centers, setCenters] = useState<MfiCenter[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchCenters = async () => {
            try {
                const result = await paymentsApi.getCenters();
                const sortedCenters = result.data.sort((a, b) => a.name.en.localeCompare(b.name.en));
                setCenters(sortedCenters);

                // If no value is set, try to restore from local storage
                if (!value) {
                    const savedSlug = localStorage.getItem(STORAGE_KEY);
                    if (savedSlug) {
                        const savedCenter = sortedCenters.find((c) => c.slug === savedSlug) || null;
                        if (savedCenter) {
                            onChange(savedSlug, savedCenter);
                        }
                    }
                }
            } catch (err) {
                console.error('Failed to load centers:', err);
            } finally {
                setLoading(false);
            }
        };
        fetchCenters();
    }, []);

    const handleChange = (e: SelectChangeEvent) => {
        const slug = e.target.value;
        const center = centers.find((c) => c.slug === slug) || null;
        // Save to local storage
        localStorage.setItem(STORAGE_KEY, slug);
        onChange(slug, center);
    };

    const lang = locale.split('-')[0] as 'en' | 'fr';

    return (
        <FormControl size={size} required={required} fullWidth={fullWidth}>
            <InputLabel id="center-select-label">{label}</InputLabel>
            <Select
                labelId="center-select-label"
                value={value}
                label={label}
                onChange={handleChange}
                disabled={disabled || loading}
                endAdornment={loading ? <CircularProgress size={20} sx={{ mr: 2 }} /> : undefined}
            >
                {centers.map((center) => {
                    const name = center.name[lang] || center.slug;
                    return (
                        <MenuItem key={center.slug} value={center.slug}>
                            {name}
                        </MenuItem>
                    );
                })}
            </Select>
        </FormControl>
    );
}
